import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { AiOutlineClose } from "react-icons/ai";
import { useLanguage } from "./LanguageContext";
import { FaGoogle } from 'react-icons/fa';
import axios from "axios";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  shadow: "0 20px 60px rgba(0,0,0,.18)",
};

/* ================= STYLES ================= */

const SocialButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: white;
  cursor: pointer;
  &:hover { background-color: #f0f0f0; }
  .social-icon { font-size: 1.2rem; color: #ffffff; }
  width: 100%;
  justify-content: center;
  align-self: center;
  justify-self: center;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
 background: rgba(17,24,39,.45);
 /* Safari/Chrome iOS compositor quirks */
 -webkit-backdrop-filter: blur(1.5px);
 backdrop-filter: blur(1.5px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity .2s ease;
  z-index: 3000;
  will-change: opacity;

 /* iOS-ზე რომ არ გათეთრდეს, პატარა ეკრანებზე blur მთლიანად ვხსნით */
 @media (max-width: 768px) {
   -webkit-backdrop-filter: none;
   backdrop-filter: none;
   background: rgba(17,24,39,.55); /* ცოტა უფრო მუქი ფონური ფერი blur-ის ნაცვლად */
 }

`;

const Modal = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  width: min(480px, 92vw);
  max-height: 90vh;
   transform: translate3d(-50%, -50%, 0); /* scale აღარ ვიყენებთ */
 opacity: ${p => (p.isAnimating && !p.isClosing ? 1 : 0)};
 transition: opacity .22s ease; /* transform-ის ანიმაცია ამოვიღეთ */
  background: #fff;
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  box-shadow: ${COLORS.shadow};
  z-index: 3001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  will-change: opacity, transform;
 backface-visibility: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid ${COLORS.line};
  background: #fff;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  color: ${COLORS.text};
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  svg { font-size: 18px; }
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,.08); }
`;

const ModalContent = styled.div`
  padding: 14px 14px 16px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: ${COLORS.brand}; border-radius: 8px; }
  overflow-x: hidden;
`;

const Section = styled.div`
  display: grid;
  gap: 10px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 900;
  color: ${COLORS.text};
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 800;
  color: ${COLORS.text};
`;

const TextInput = styled.input`
  width: 94%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  color: ${COLORS.text};
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:focus { border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.15); }
`;

const Select = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  background: #fff;
  font-size: 14px;
  color: ${COLORS.text};
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:focus { border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.15); }
`;

const Helper = styled.small`
  color: ${COLORS.dim};
  font-size: 12px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 0;
  background: ${COLORS.text};
  color: #fff;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease;
  &:hover { transform: translateY(-1px); filter: brightness(.96); }
  &:disabled { opacity: .6; cursor: not-allowed; transform: none; }
`;

const GhostButton = styled(Button)`
  background: #fff;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.line};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 6px;
`;

const TextLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${COLORS.brand};
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLORS.line};
  margin: 10px 0;
`;

const ErrorPopupWrap = styled.div`
  position: fixed;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #fff3f3, #ffe6e6);
  color: ${COLORS.brandDark};
  padding: 10px 14px;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 3200;
  max-width: 90%;
  min-width: 260px;
  font-size: 13px;
`;

const ErrorClose = styled.button`
  width: 26px; height: 26px;
  border-radius: 50%;
  border: 0; background: ${COLORS.brandDark};
  color: #fff; font-size: 16px; line-height: 26px;
  cursor: pointer;
`;

const CardsBox = styled.div`
  margin-top: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const CardsHeader = styled.div`
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${COLORS.subtle};
  border-bottom: 1px solid ${COLORS.line};
`;

const CardsTitle = styled.div`
  font-weight: 900;
  font-size: 13px;
  color: ${COLORS.text};
`;

const CardRow = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid ${COLORS.line};
  &:last-child { border-bottom: 0; }
`;

const CardLeft = styled.div`
  display: grid;
  gap: 2px;
`;

const CardName = styled.div`
  font-weight: 900;
  color: ${COLORS.text};
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 900;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(161,90,90,.12);
  color: ${COLORS.brandDark};
  border: 1px solid rgba(161,90,90,.25);
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: ${COLORS.dim};
`;

const TinyBtn = styled.button`
  height: 32px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  color: ${COLORS.text};
  font-weight: 900;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
  &:disabled { opacity: .55; cursor: not-allowed; transform: none; box-shadow: none; }
`;

/* ================= Error Popup ================= */
const ErrorPopup = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <ErrorPopupWrap>
      <span style={{ flex: 1 }}>{message}</span>
      <ErrorClose onClick={onClose} title="Close">×</ErrorClose>
    </ErrorPopupWrap>
  );
};

/* ================= Component ================= */
const RegisterUser = ({ isRegistering: initialIsRegistering, toggleModal, isModalOpen }) => {
  const [formData, setFormData] = useState({
    name: "", surname: "", email: "", phone: "",
    password: "", confirmPassword: "",
    day: "", month: "", year: "",
    phone_country_iso: "GE",
    phone_country_code: "995",
  });

  const parseBirth = (dateStr) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return { birth_date: "", day: "", month: "", year: "" };
    }
    const [y,m,d] = dateStr.split("-");
    return { birth_date: dateStr, day: d, month: m, year: y };
  };
  const [birthDate, setBirthDate] = useState("");
  const birth = parseBirth(birthDate);

  const [isRegistering, setIsRegistering] = useState(initialIsRegistering);
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const { t, language } = useLanguage();

  const navigate = useNavigate();
  const location = useLocation();

  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const [popupError, setPopupError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState("");

  // OTP flow
  const [step, setStep] = useState("form"); // 'form' | 'otp' | 'done'
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [finalMessage, setFinalMessage] = useState("");

    // Saved cards
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(0);

    /* ================= Validation ================= */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;


  // Resend complete registration link (Login view)
const [resendOpen, setResendOpen] = useState(false);
const [resendLoading, setResendLoading] = useState(false);
const [resendMsg, setResendMsg] = useState("");



  const fetchCards = async () => {
    if (!user) return;
    setCardsLoading(true);
    try {
      const res = await api.get(`${API_BASE_URL}/payment_methods/list.php`, { withCredentials: true });
      setCards(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setCards([]);
    } finally {
      setCardsLoading(false);
    }
  };

  useEffect(() => {
  if (isModalOpen && user) fetchCards();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isModalOpen, user?.email, userDetails?.id]);


  const deleteCard = async (id) => {
    if (!id) return;
    const ok = window.confirm(language === "ka" ? "წავშალოთ ეს ბარათი?" : "Delete this card?");
    if (!ok) return;

    setDeletingId(id);
    try {
      const res = await api.post(`${API_BASE_URL}/payment_methods/delete.php`, { id }, { withCredentials: true });
      if (res.data?.success) {
        setCards((prev) => prev.filter((c) => c.id !== id));
        setPopupError(language === "ka" ? "ბარათი წაიშალა" : "Card removed");
      } else {
        setPopupError(language === "ka" ? "ვერ წაიშალა" : "Delete failed");
      }
    } catch {
      setPopupError(language === "ka" ? "სერვერის შეცდომა" : "Server error");
    } finally {
      setDeletingId(0);
    }
  };


  // ---------- scroll lock helpers ----------
  const lockScroll = () => {
    const b = document.body;
    b.style.overflow = "hidden";
    b.style.position = "relative";
    b.style.width = "100%";
    document.body.style.overflow = "hidden";
  };
  const unlockScroll = () => {
    const b = document.body;
    b.style.overflow = "";
    b.style.position = "";
    b.style.width = "";
    document.body.style.overflow = "";
  };

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer(s => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  useEffect(() => {
    if (isModalOpen) {
      setIsAnimating(true);
      lockScroll();
    } else {
      setTimeout(() => setIsAnimating(false), 220);
      unlockScroll();
    }
  }, [isModalOpen]);

  // უსაფრთხოების მიზნით: ნებისმიერ როუტ ცვლაზე გაათავისუფლე scroll-lock
  useEffect(() => {
    unlockScroll();
    setIsClosing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      unlockScroll();
    };
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && storedUser !== "undefined") {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      fetchUserDetails(userData.email);
    }
  }, []);

  useEffect(() => {
    if (user?.email) fetchUserDetails(user.email);
  }, [user]);

  const fetchUserDetails = async (email) => {
    try {
      const res = await api.post(`${API_BASE_URL}/get_user_details.php`, { email });
      if (res.data?.user) setUserDetails(res.data.user);
    } catch (err) {
      console.error("Error fetching user details:", err);
    }
  };

  const COUNTRIES = [
    { iso: "GE", name: "Georgia (+995)", dial: "995", min: 9, max: 9 },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(s => ({ ...s, [name]: value }));
  };


  const isValidPhoneForCountry = (iso, phone) => {
    const onlyDigits = (phone || '').replace(/\D+/g, '');
    const c = COUNTRIES.find(x => x.iso === iso) || COUNTRIES[0];
    if (onlyDigits.length < c.min || onlyDigits.length > c.max) return false;
    if (iso === 'GE') return onlyDigits.length === 9 && onlyDigits.startsWith('5');
    return true;
  };

  const validateForm = () => {
    const errors = [];
    if (!emailRegex.test(formData.email)) {
      errors.push(language === "ka" ? "გთხოვთ შეიყვანოთ სწორი იმეილი" : "Please enter a valid email");
    }
    if (!/^\d+$/.test(formData.phone)) {
      errors.push(language === "ka" ? "ტელეფონის ნომერი უნდა იყოს მხოლოდ ციფრები" : "Phone must be digits only");
    } else if (!isValidPhoneForCountry(formData.phone_country_iso, formData.phone)) {
      errors.push(language === "ka"
        ? "საქართველოს ნომერი უნდა იწყებოდეს 5-ით და იყოს ზუსტად 9 ციფრი"
        : "For Georgia, phone must start with 5 and be exactly 9 digits");
    }
    if (!passwordRegex.test(formData.password)) {
      errors.push(language === "ka"
        ? "პაროლი უნდა შეიცავდეს დიდ ასოს, ციფრს, სიმბოლოს და იყოს მინიმუმ 8 სიმბოლო"
        : "Password must include uppercase letter, number, symbol and be at least 8 characters");
    }
    if (formData.password !== formData.confirmPassword) {
      errors.push(language === "ka"
        ? "პაროლი და გამეორება არ ემთხვევა"
        : "Password and confirmation do not match");
    }
    if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate) || Number.isNaN(+new Date(birthDate))) {
      errors.push(language === "ka" ? "შეიყვანეთ დაბადების თარიღი (YYYY-MM-DD)" : "Enter birth date (YYYY-MM-DD)");
    }
    return errors;
  };

  /* ================= Server error mapping ================= */
  const normalizeServerPayload = (maybe) => {
    if (maybe && typeof maybe === "object") return maybe;
    if (typeof maybe === "string") { try { return JSON.parse(maybe); } catch { return { error: maybe }; } }
    return { error: String(maybe || "") };
  };
  const mapServerErrorToLocale = (rawPayload, lang) => {
    const p = normalizeServerPayload(rawPayload);
    const code = p?.error_code ? String(p.error_code) : "";
    const raw  = p?.error ? String(p.error) : "";
    const e = raw.toLowerCase();
    switch (code) {
      case "EMAIL_EXISTS": return lang==="ka"?"მითითებული იმეილი უკვე არსებობს":"Email already exists";
      case "PHONE_EXISTS": return lang==="ka"?"მითითებული ტელეფონის ნომერი უკვე არსებობს":"Phone number already exists";
      case "INVALID_EMAIL": return lang==="ka"?"არასწორი ელფოსტა":"Invalid email";
      case "INVALID_PHONE": return lang==="ka"?"ტელეფონის ნომერი არასწორია":"Invalid phone number";
      case "INVALID_BIRTHDATE": return lang==="ka"?"არასწორი დაბადების თარიღი":"Invalid birth date";
      case "WEAK_PASSWORD": return lang==="ka"?"სუსტი პაროლი":"Weak password";
      case "SAVE_FAILED": return lang==="ka"?"ვერ შევინახეთ მონაცემები":"Failed to save user.";
      case "BAD_JSON": return lang==="ka"?"არასწორი მონაცემები":"Invalid input";
      default: break;
    }
    if (e.includes("email already")) return lang==="ka"?"მითითებული იმეილი უკვე არსებობს":"Email already exists";
    if (e.includes("phone") && e.includes("exist")) return lang==="ka"?"მითითებული ტელეფონის ნომერი უკვე არსებობს":"Phone number already exists";
    if (e.includes("invalid email")) return lang==="ka"?"არასწორი ელფოსტა":"Invalid email";
    if (e.includes("invalid phone")) return lang==="ka"?"ტელეფონის ნომერი არასწორია":"Invalid phone number";
    return lang==="ka"?"რეგისტრაცია ვერ განხორციელდა":"Registration failed.";
  };

  /* ================= SMS helpers ================= */
  const fullE164 = () => `+${formData.phone_country_code}${formData.phone}`.replace(/\s+/g, "");

  const requestSmsCode = async () => {
    setSending(true);
    try {
      const r = await api.post(`${API_BASE_URL}/auth/request_sms_code.php`, {
        phone: fullE164()
      }, { headers: { "Content-Type": "application/json" }, validateStatus: () => true });

      if (r.data?.ok) {
        setPopupError(language==="ka"?"კოდი გამოიგზავნა":"Code sent");
        setStep("otp");
        setResendTimer(60);
      } else {
        const reason =
          r.data?.msg ||
          r.data?.debug?.response ||
          (language==="ka" ? "ვერ გაიგზავნა" : "Send failed");
        setPopupError(reason);
      }
    } catch (e) {
      setPopupError(language==="ka"?"სერვერის შეცდომა":"Server error");
    } finally {
      setSending(false);
    }
  };

  const verifySmsCode = async () => {
    if (code.length !== 6) return;
    setSending(true);
    try {
      const v = await api.post(`${API_BASE_URL}/auth/verify_sms_code.php`, {
        phone: fullE164(),
        code
      }, { headers: { "Content-Type": "application/json" }, validateStatus: () => true });

      if (!v.data?.ok) {
        setPopupError(v.data?.msg || (language==="ka"?"კოდი არასწორია":"Wrong code"));
        return;
      }

      setPopupError("");

      const { data } = await api.post(
        `${API_BASE_URL}/register_user.php`,
        {
          ...formData,
          phone_country_iso: formData.phone_country_iso,
          phone_country_code: formData.phone_country_code,
          phone_e164: fullE164(),
          lang: language,
          birth_date: birth.birth_date,
          day: birth.day, month: birth.month, year: birth.year,
        },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );

      if (data?.success) {
      const msg = language==="ka"
        ? "რეგისტრაცია დასრულებულია ✅ გთხოვთ, გადაამოწმოთ ელფოსტა (დასტურის ბმული გაიგზავნა)."
        : "Registration complete ✅ Please check your email (a confirmation link was sent).";
         setTimeout(() => {
          try { document.body.style.overflow = ""; } catch (_) {}
          if (typeof toggleModal === "function") toggleModal();
          navigate("/registration-success", {
            replace: true,
            state: {
              message: msg,
              email: formData.email,
              phone: `+${formData.phone_country_code}${formData.phone}`
            }
          });
        }, 0);
      } else {
        setPopupError(mapServerErrorToLocale(data, language));
      }
    } catch (e) {
      setPopupError(language==="ka"?"სერვერის შეცდომა":"Server error");
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    if (resendTimer > 0 || sending) return;
    await requestSmsCode();
  };

  /* ================= Submit handlers ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setPopupError(errors[0]);
      return;
    }
    await requestSmsCode();
  };

  const handleAuthentication = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await api.post(`${API_BASE_URL}/login_user.php`, {
        email: formData.email, password: formData.password,
      }, { withCredentials: true });

      if (response.data?.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        window.dispatchEvent(new Event("storage"));
        setUser(response.data.user);
        window.location.reload();
      } else {
        alert(response.data?.error || "Login failed.");
      }
    } catch (err) {
      alert("Login failed.");
    }
  };

  const handleLogout = async () => {
    try {
      await api.post(`${API_BASE_URL}/logout.php`, {}, { withCredentials: true });
    } finally {
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("storage"));
      setUser(null);
      window.location.reload();
    }
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    setIsClosing(true);
    setTimeout(() => { toggleModal(); setIsClosing(false); unlockScroll(); }, 220);
  };

    const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (!formData.email) { setErrorMessage("Please enter your email."); return; }
    try {
      const res = await api.post(`${API_BASE_URL}/forgot_password.php`, {
        email: formData.email, language
      });
      if (res.data?.success) {
        alert(language==="ka" ? "პაროლის აღდგენის ბმული გაიგზავნა ელფოსტაზე." : "Password reset email was sent.");
        setIsForgotPassword(false);
      } else {
        setErrorMessage(res.data?.error || "Failed to send reset email.");
      }
    } catch {
      setErrorMessage("Error sending password reset email.");
    }
  };


  // დახურე მოდალი და შემდეგ გადადი (პლიუს state, როცა გვჭირდება)
  const closeThenGo = (to, options = {}) => (e) => {
    e.preventDefault();
    setIsClosing(true);
    setTimeout(() => {
      toggleModal();
      setIsClosing(false);
      unlockScroll();
      navigate(to, options);
    }, 220);
  };

  const resendCompleteLink = async () => {
  setResendMsg("");
  setPopupError("");

  const email = (formData.email || "").trim();
  if (!emailRegex.test(email)) {
    setPopupError(t("login.invalid_email"));
    return;
  }

  setResendLoading(true);
  try {
    const r = await api.post(
      `${API_BASE_URL}/resend_complete_registration.php`,
      { email, lang: language },
      { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
    );

    if (r.data?.success) {
      // ALREADY_ACTIVE -> route to forgot password suggestion
      if (r.data?.action === "ALREADY_ACTIVE") {
        setResendMsg(r.data?.message || t("login.account_already_active"));
      } else {
        setResendMsg(r.data?.message || t("login.resend_complete_sent"));
      }
      setResendOpen(true);
    } else {
      setPopupError(r.data?.error || (language === "ka" ? "შეცდომა" : "Error"));
    }
  } catch (e) {
    setPopupError(language === "ka" ? "სერვერის შეცდომა" : "Server error");
  } finally {
    setResendLoading(false);
  }
};


  /* ================= Render ================= */
  const phoneIsValid = isValidPhoneForCountry(formData.phone_country_iso, formData.phone);

  return (
    <>
      <ErrorPopup message={popupError} onClose={() => setPopupError("")} />
      {isModalOpen && (
        <Overlay
          $open={isModalOpen}
          onClick={step === "form" ? handleCloseModal : undefined}
        />
      )}

      {isModalOpen && (
        <Modal onClick={(e) => e.stopPropagation()} isAnimating={isAnimating} isClosing={isClosing}>
          <ModalHeader>
            <Title>
              {user
                ? t("login.userinfo")
                : isRegistering
                  ? (isForgotPassword ? t("login.forgotpassword") : t("login.register"))
                  : t("login.authorisation")}
            </Title>
            <CloseBtn onClick={handleCloseModal} aria-label="Close modal">
              <AiOutlineClose />
            </CloseBtn>
          </ModalHeader>

          <ModalContent>
            {/* Forgot Password */}
            {isForgotPassword ? (
              <Section>
                {errorMessage && <div style={{ color: "#e11d48", fontSize: 13 }}>{errorMessage}</div>}
                <form onSubmit={handleForgotPassword} style={{ display: "grid", gap: 10 }}>
                  <Label>{t("login.email")}:</Label>
                  <TextInput type="email" name="email" value={formData.email} onChange={handleChange} required />
                  <Button type="submit">{t("login.sendresetlink")}</Button>
                  <TextLink type="button" onClick={() => setIsForgotPassword(false)}>
                    {t("login.backtologin")}
                  </TextLink>
                </form>
              </Section>
            ) : user ? (
              /* Logged-in user info */
              <Section>
                <div style={{ display: "grid", gap: 6, fontSize: 14, color: COLORS.text }}>
                  <div><strong>{t("login.name")}:</strong> {user?.name}</div>
                  <div><strong>{t("login.surname")}:</strong> {userDetails?.surname}</div>
                  <div><strong>{t("login.email")}:</strong> {user?.email}</div>
                  {userDetails?.phone && <div><strong>{t("login.phone")}:</strong> {userDetails.phone}</div>}
                  {userDetails?.birth_date && <div><strong>{t("login.birthdate")}:</strong> {userDetails.birth_date}</div>}
                </div>

                <CardsBox>
                  <CardsHeader>
                    <CardsTitle>{language==="ka" ? "დამახსოვრებული ბარათები" : "Saved cards"}</CardsTitle>
                    <div style={{ fontSize: 12, color: COLORS.dim }}>
                      {cardsLoading ? (language==="ka" ? "იტვირთება..." : "Loading...") : ""}
                    </div>
                  </CardsHeader>

                  {cardsLoading ? (
                    <div style={{ padding: 12, color: COLORS.dim, fontSize: 13 }}>
                      {language==="ka" ? "ბარათების სია იტვირთება..." : "Loading cards..."}
                    </div>
                  ) : (cards?.length ? (
                    cards.map((c) => (
                      <CardRow key={c.id}>
                        <CardLeft>
                          <CardName>
                            {(c.brand || "Card")} •••• {c.last4 || "----"}
                            {c.is_default === 1 ? <Badge>{language==="ka" ? "ძირითადი" : "Default"}</Badge> : null}
                          </CardName>
                          <CardMeta>
                            {c.exp_month && c.exp_year
                              ? `${String(c.exp_month).padStart(2,"0")}/${String(c.exp_year).slice(-2)}`
                              : (language==="ka" ? "ვადა: უცნობია" : "Expiry: n/a")}
                          </CardMeta>
                        </CardLeft>

                        <TinyBtn
                          onClick={() => deleteCard(c.id)}
                          disabled={deletingId === c.id}
                          title={language==="ka" ? "წაშლა" : "Remove"}
                        >
                          {deletingId === c.id ? (language==="ka" ? "..." : "...") : (language==="ka" ? "წაშლა" : "Remove")}
                        </TinyBtn>
                      </CardRow>
                    ))
                  ) : (
                    <div style={{ padding: 12, color: COLORS.dim, fontSize: 13 }}>
                      {language==="ka"
                        ? "დამახსოვრებული ბარათი არ გაქვთ."
                        : "You have no saved cards."}
                    </div>
                  ))}
                </CardsBox>


                <Divider />

                <LinkRow>
                  <GhostButton
                    as="button"
                    style={{ width: 180 }}
                    onClick={closeThenGo("/my-purchases")}
                  >
                    {t("menu.mypurchases")}
                  </GhostButton>

                  {userDetails?.has_password && (
                    <GhostButton
                      as="button"
                      style={{ width: 180 }}
                      onClick={closeThenGo("/change-password", { state: { user } })} // ← state გადავეცით
                    >
                      {t("login.changepassword")}
                    </GhostButton>
                  )}
                </LinkRow>

                <Button style={{ marginTop: 8 }} onClick={handleLogout}>{t("login.logout")}</Button>
              </Section>
            ) : isRegistering ? (
              /* Registration stepper */
              step === "form" ? (
                <Section>
                  <SectionTitle>{t("login.register")}</SectionTitle>
                  <form onSubmit={handleSubmit} style={{ display: "grid", gap: 10 }}>
                    <Label>{t("login.name")}:</Label>
                    <TextInput type="text" name="name" value={formData.name} onChange={handleChange} required />

                    <Label>{t("login.surname")}:</Label>
                    <TextInput type="text" name="surname" value={formData.surname} onChange={handleChange} required />

                    <Label>{t("login.email")}:</Label>
                    <TextInput type="email" name="email" value={formData.email} onChange={handleChange} required />

                    <Label>{t("login.country")}:</Label>
                    <Select
                      name="phone_country_iso"
                      value={formData.phone_country_iso}
                      onChange={(e) => {
                        const iso = e.target.value;
                        const c = COUNTRIES.find(x => x.iso === iso) || COUNTRIES[0];
                        setFormData(prev => ({ ...prev, phone_country_iso: iso, phone_country_code: c.dial }));
                      }}
                      required
                    >
                      {COUNTRIES.map(c => <option key={c.iso} value={c.iso}>{c.name}</option>)}
                    </Select>

                    <Label>{t("login.phone")}:</Label>
                    <TextInput
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D+/g, "");
                        setFormData(prev => ({ ...prev, phone: v }));
                      }}
                      required
                    />
                    <Helper>{t("login.example")} {` +${formData.phone_country_code} ${formData.phone_country_iso==='GE' ? '5XXXXXXXX' : formData.phone}`}</Helper>

                    <Label>{t("login.password")}:</Label>
                    <TextInput type="password" name="password" value={formData.password} onChange={handleChange} required />

                    <Label>{t("login.confirmpassword")}:</Label>
                    <TextInput type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />

                    <Label>{t("login.birthdate")}:</Label>
                    <TextInput type="date" value={birthDate} onChange={(e)=>setBirthDate(e.target.value)} />

                    <Button type="submit" disabled={sending || !phoneIsValid}>
                      {sending ? (language==="ka"?"იგზავნება...":"Sending...") : t("login.registration")}
                    </Button>

                    <TextLink type="button" onClick={() => setIsRegistering(false)}>{t("login.alreadyregistered")}</TextLink>
                  </form>
                </Section>
              ) : step === "otp" ? (
                <Section>
                  <SectionTitle>{language==="ka" ? "შეიყვანე SMS კოდი" : "Enter SMS Code"}</SectionTitle>
                  <div style={{ color: COLORS.dim, fontSize: 13, marginBottom: 6 }}>
                    {language==="ka" ? "კოდი გამოგიგზავნეთ ნომერზე:" : "We sent a code to:"} {fullE164()}
                  </div>

                  <TextInput
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    placeholder={language==="ka" ? "6-ნიშნა კოდი" : "6-digit code"}
                    value={code}
                    onChange={(e)=>setCode(e.target.value.replace(/\D/g,""))}
                    style={{ letterSpacing: 2, textAlign: "center" }}
                  />

                  <Button onClick={verifySmsCode} disabled={sending || code.length!==6}>
                    {sending ? (language==="ka"?"მიმდინარეობს...":"Processing...") : (language==="ka"?"დადასტურება":"Verify")}
                  </Button>

                  <GhostButton onClick={resend} disabled={resendTimer>0 || sending} style={{ marginTop: 6 }}>
                    {language==="ka" ? "ხელახლა გაგზავნა" : "Resend"} {resendTimer>0 ? `(${resendTimer}s)` : ""}
                  </GhostButton>
                </Section>
              ) : (
   <Section>
     <SectionTitle>{language==="ka" ? "დასრულებულია" : "All set"}</SectionTitle>
     <div
       style={{
         color: COLORS.text,
         fontSize: 14,
         background: "#f5fff5",
         border: "1px solid #b6e3b6",
         padding: "10px 12px",
         borderRadius: 10
       }}
     >
       {finalMessage || (language==="ka"
         ? "რეგისტრაცია დასრულდა. გადაამოწმე ელფოსტა."
         : "Registration completed. Please check your email.")}
     </div>
   </Section>
              )
            ) : (
              /* Login */
              <Section>
                <SectionTitle>{t("login.authorisation")}</SectionTitle>
                <form onSubmit={handleAuthentication} style={{ display: "grid", gap: 10 }}>
                  <Label>{t("login.email")}:</Label>
                  <TextInput type="email" name="email" value={formData.email} onChange={handleChange} required />

                  <Label>{t("login.password")}:</Label>
                  <TextInput type="password" name="password" value={formData.password} onChange={handleChange} required />

                  <Button type="submit">{t("login.authorisation")}</Button>
<SocialButton
  type="button"
  onClick={() => window.location.assign("/api/google_callback.php")}
>                    <FaGoogle className="social-icon" color="black" />
                    {t("login.google")}
                  </SocialButton>

                  <LinkRow>
                    <TextLink type="button" onClick={() => setIsRegistering(true)}>{t("login.notregisteredyet")}</TextLink>
                    <TextLink type="button" onClick={() => setIsForgotPassword(true)}>{t("login.forgotpassword")}</TextLink>
                  </LinkRow>
                                  <Divider />
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 900, fontSize: 13, color: COLORS.text }}>
                      {t("login.resend_complete_title")}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.dim, lineHeight: 1.35 }}>
                      {t("login.resend_complete_hint")}
                    </div>

                    <GhostButton
                      type="button"
                      onClick={resendCompleteLink}
                      disabled={resendLoading}
                      style={{ marginTop: 4 }}
                    >
                      {resendLoading ? t("login.sending") : t("login.resend_complete_btn")}
                    </GhostButton>

                    {resendMsg ? (
                      <div style={{ marginTop: 6, color: "#0b7a2a", fontWeight: 800, fontSize: 13 }}>
                        {resendMsg}
                      </div>
                    ) : null}
                  </div>
                </form>
              </Section>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default RegisterUser;
